/**
 * RewardsPanel - Panneau de récompenses gamifiées
 *
 * Affiche les récompenses disponibles et permet de les utiliser
 * pour encourager l'accomplissement des tâches
 */

import React, { useState, memo, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X, Gift, Star, Heart, Lock, Unlock, Play, Pause,
  Music, Brain, Coffee, Sparkles, ChevronRight, Crown,
  Coins, Clock, Check, Headphones
} from 'lucide-react';
import { BottomSheet } from '../../../components/ui/BottomSheet';

// Icônes par catégorie
const CATEGORY_ICONS = {
  music: Headphones,
  meditation: Brain,
  breaks: Coffee,
  special: Sparkles,
};

/**
 * Carte de récompense individuelle
 */
const RewardCard = memo(function RewardCard({
  reward,
  isUnlocked,
  isFavorite,
  canAfford,
  onUse,
  onToggleFavorite,
}) {
  const isAvailable = isUnlocked || canAfford;

  return (
    <motion.div
      layout
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9 }}
      className={`
        relative p-4 rounded-2xl border transition-all
        ${isAvailable
          ? 'bg-zinc-800/80 border-zinc-700/50 hover:border-zinc-600'
          : 'bg-zinc-900/50 border-zinc-800/50 opacity-60'
        }
      `}
    >
      {/* Badge de statut */}
      {isUnlocked && (
        <div className="absolute -top-2 -right-2 w-6 h-6 bg-emerald-500 rounded-full flex items-center justify-center">
          <Unlock className="w-3 h-3 text-white" />
        </div>
      )}

      <div className="flex items-start gap-3">
        {/* Icône */}
        <div className={`
          w-12 h-12 rounded-xl flex items-center justify-center text-2xl
          ${isAvailable
            ? 'bg-gradient-to-br from-purple-500/30 to-pink-500/30'
            : 'bg-zinc-800'
          }
        `}>
          {reward.icon}
        </div>

        {/* Contenu */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="font-semibold text-white truncate">
              {reward.name}
            </h3>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onToggleFavorite(reward.id);
              }}
              className="flex-shrink-0"
            >
              <Heart
                className={`w-4 h-4 transition-colors ${
                  isFavorite ? 'fill-red-500 text-red-500' : 'text-zinc-500'
                }`}
              />
            </button>
          </div>

          <p className="text-xs text-zinc-400 mb-2 line-clamp-1">
            {reward.preview}
          </p>

          <div className="flex items-center gap-3">
            {/* Durée */}
            {reward.duration > 0 && (
              <span className="text-xs text-zinc-500 flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {reward.duration} min
              </span>
            )}

            {/* Coût */}
            <span className={`
              text-xs font-medium flex items-center gap-1
              ${canAfford ? 'text-yellow-400' : 'text-zinc-500'}
            `}>
              <Coins className="w-3 h-3" />
              {reward.cost}
            </span>
          </div>
        </div>

        {/* Bouton d'action */}
        <button
          onClick={() => onUse(reward.id)}
          disabled={!isAvailable}
          className={`
            w-10 h-10 rounded-xl flex items-center justify-center transition-all
            ${isAvailable
              ? 'bg-gradient-to-br from-purple-500 to-pink-500 text-white hover:scale-105'
              : 'bg-zinc-800 text-zinc-600'
            }
          `}
        >
          {isAvailable ? <Play className="w-5 h-5" /> : <Lock className="w-4 h-4" />}
        </button>
      </div>
    </motion.div>
  );
});

/**
 * Section de catégorie
 */
const CategorySection = memo(function CategorySection({
  category,
  categoryKey,
  rewards,
  isUnlocked,
  isFavorite,
  canAfford,
  onUse,
  onToggleFavorite,
}) {
  const [isExpanded, setIsExpanded] = useState(true);
  const Icon = CATEGORY_ICONS[categoryKey] || Gift;

  return (
    <div className="mb-6">
      {/* En-tête de catégorie */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center gap-3 mb-3"
      >
        <div className={`
          w-8 h-8 rounded-lg flex items-center justify-center
          bg-gradient-to-br ${category.color}
        `}>
          <Icon className="w-4 h-4 text-white" />
        </div>
        <span className="font-semibold text-white">{category.name}</span>
        <span className="text-xs text-zinc-500 ml-auto">
          {rewards.length} disponibles
        </span>
        <ChevronRight className={`
          w-4 h-4 text-zinc-500 transition-transform
          ${isExpanded ? 'rotate-90' : ''}
        `} />
      </button>

      {/* Liste des récompenses */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="space-y-3 overflow-hidden"
          >
            {rewards.map(reward => (
              <RewardCard
                key={reward.id}
                reward={reward}
                isUnlocked={isUnlocked(reward.id)}
                isFavorite={isFavorite(reward.id)}
                canAfford={canAfford(reward.id)}
                onUse={onUse}
                onToggleFavorite={onToggleFavorite}
              />
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
});

/**
 * Panneau principal de récompenses
 */
export const RewardsPanel = memo(function RewardsPanel({
  isOpen,
  onClose,
  availableCoins,
  categories,
  catalog,
  getByCategory,
  isUnlocked,
  isFavorite,
  canUseReward,
  onUseReward,
  toggleFavorite,
  getRecommendations,
  getFavorites,
  checkDailyFreeReward,
  onUseDailyFreeReward,
}) {
  const [activeTab, setActiveTab] = useState('recommended');

  // Récompenses recommandées
  const recommendations = useMemo(() => getRecommendations(), [getRecommendations]);

  // Favoris
  const favorites = useMemo(() => getFavorites(), [getFavorites]);

  // Récompense gratuite du jour
  const dailyFreeReward = useMemo(() => checkDailyFreeReward(), [checkDailyFreeReward]);

  // Handler pour utiliser une récompense
  const handleUseReward = (rewardId) => {
    const result = onUseReward(rewardId);
    if (result.success) {
      onClose();
    }
  };

  // Handler pour la récompense gratuite
  const handleUseFreeReward = () => {
    const result = onUseDailyFreeReward();
    if (result.success) {
      onClose();
    }
  };

  return (
    <BottomSheet
      isOpen={isOpen}
      onClose={onClose}
      title="Récompenses"
      height="85vh"
    >
      <div className="flex flex-col h-full">
        {/* Header avec coins */}
        <div className="flex items-center justify-between px-4 pb-4 border-b border-zinc-800">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-yellow-500 to-orange-500 flex items-center justify-center">
              <Coins className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{availableCoins}</p>
              <p className="text-xs text-zinc-500">Coins disponibles</p>
            </div>
          </div>

          {/* Récompense gratuite du jour */}
          {dailyFreeReward && (
            <button
              onClick={handleUseFreeReward}
              className="px-4 py-2 bg-gradient-to-r from-emerald-500 to-cyan-500 rounded-xl
                flex items-center gap-2 text-white font-medium animate-pulse"
            >
              <Gift className="w-4 h-4" />
              <span className="text-sm">Cadeau gratuit !</span>
            </button>
          )}
        </div>

        {/* Tabs */}
        <div className="flex gap-2 p-4 overflow-x-auto">
          {[
            { id: 'recommended', label: 'Pour toi', icon: Star },
            { id: 'favorites', label: 'Favoris', icon: Heart },
            { id: 'all', label: 'Tout voir', icon: Gift },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`
                flex items-center gap-2 px-4 py-2 rounded-xl whitespace-nowrap transition-all
                ${activeTab === tab.id
                  ? 'bg-purple-500/20 text-purple-400 border border-purple-500/30'
                  : 'bg-zinc-800/50 text-zinc-400 border border-transparent'
                }
              `}
            >
              <tab.icon className="w-4 h-4" />
              <span className="text-sm font-medium">{tab.label}</span>
            </button>
          ))}
        </div>

        {/* Contenu */}
        <div className="flex-1 overflow-y-auto px-4 pb-8">
          {/* Tab: Recommandations */}
          {activeTab === 'recommended' && (
            <div>
              <p className="text-xs text-zinc-500 mb-4">
                Basé sur l'heure et ton activité
              </p>
              <div className="space-y-3">
                {recommendations.map(reward => (
                  <RewardCard
                    key={reward.id}
                    reward={reward}
                    isUnlocked={isUnlocked(reward.id)}
                    isFavorite={isFavorite(reward.id)}
                    canAfford={canUseReward(reward.id)}
                    onUse={handleUseReward}
                    onToggleFavorite={toggleFavorite}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Tab: Favoris */}
          {activeTab === 'favorites' && (
            <div>
              {favorites.length === 0 ? (
                <div className="text-center py-12">
                  <Heart className="w-12 h-12 text-zinc-700 mx-auto mb-3" />
                  <p className="text-zinc-500">Pas encore de favoris</p>
                  <p className="text-xs text-zinc-600 mt-1">
                    Touche le ❤️ pour ajouter des favoris
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {favorites.map(reward => (
                    <RewardCard
                      key={reward.id}
                      reward={reward}
                      isUnlocked={isUnlocked(reward.id)}
                      isFavorite={true}
                      canAfford={canUseReward(reward.id)}
                      onUse={handleUseReward}
                      onToggleFavorite={toggleFavorite}
                    />
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Tab: Toutes les récompenses */}
          {activeTab === 'all' && (
            <div>
              {Object.entries(categories).map(([key, category]) => (
                <CategorySection
                  key={key}
                  categoryKey={key}
                  category={category}
                  rewards={getByCategory(key)}
                  isUnlocked={isUnlocked}
                  isFavorite={isFavorite}
                  canAfford={canUseReward}
                  onUse={handleUseReward}
                  onToggleFavorite={toggleFavorite}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </BottomSheet>
  );
});

export default RewardsPanel;
